import { createFileRoute, Link } from "@tanstack/react-router";
import { CompactNoteForm } from "@/components/compact-note-form";
import { useQuery } from "@tanstack/react-query";
import { notesQueryOptions } from "@/queries/notes";
import { noteListsQueryOptions } from "@/queries/noteLists";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Inbox as InboxIcon,
  CheckCircle2,
  FolderPlus,
  Loader2,
} from "lucide-react";
import { useMemo } from "react";
import { useDocumentTitle } from "usehooks-ts";
import { useNoteCreation } from "@/hooks/useNoteCreation";
import { useAddNoteToList } from "@/mutations/noteLists";
import { Badge } from "@/components/ui/badge";
import { SimpleNoteTable } from "@/components/note-lists/simple-note-table";
import { PageHeader } from "@/components/layout/page-header";
import { UnauthenticatedPage } from "@/components/layout/unauthenticated-page";
import type { Note } from "@/types/note";
import { toast } from "sonner";

export const Route = createFileRoute("/inbox")({
  component: InboxView,
});

function InboxView() {
  const { user } = Route.useRouteContext();
  useDocumentTitle("Inbox | Notebook");

  const { data: notes, isLoading: isLoadingNotes } = useQuery({
    ...notesQueryOptions(user?.id),
    enabled: !!user?.id,
  });

  const { data: lists = [], isLoading: isLoadingLists } = useQuery({
    ...noteListsQueryOptions(user?.id),
    enabled: !!user?.id,
  });

  const { handleNoteCreated, isPending: isCreateNotePending } = useNoteCreation(
    user?.id || "",
  );

  const addNoteToList = useAddNoteToList();

  // Filter to only show notes not assigned to any list
  const unassignedNotes = useMemo(() => {
    if (!notes) return [];
    return notes.filter(
      (note: Note) => !note.noteLists || note.noteLists.length === 0,
    );
  }, [notes]);

  const handleAddToList = async (noteId: string, listId: string) => {
    const list = lists.find((l) => l.id === listId);
    try {
      await addNoteToList.mutateAsync({ listId, noteId });
      toast.success(`Added to ${list?.name || "list"}`);
    } catch {
      toast.error("Failed to add to list");
    }
  };

  if (!user?.id) {
    return (
      <UnauthenticatedPage
        title="Inbox"
        description="Sign in to organize your notes"
        icon={InboxIcon}
        iconColor="text-blue-500"
      />
    );
  }

  const isLoading = isLoadingNotes || isLoadingLists;

  if (isLoading) {
    return (
      <>
        <PageHeader title="Inbox" icon={InboxIcon} iconColor="text-blue-500" />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Inbox"
        icon={InboxIcon}
        iconColor="text-blue-500"
        actions={
          <Button
            variant="outline"
            size="sm"
            asChild
            className="text-xs md:text-sm"
          >
            <Link to="/">
              <span className="hidden md:inline">All Notes</span>
              <span className="md:hidden">All</span>
            </Link>
          </Button>
        }
      />

      <div className="animate-fade-in max-w-4xl mx-auto w-full mb-6 md:mb-8 py-3 md:py-4 px-4">
        <CompactNoteForm
          onSubmit={handleNoteCreated}
          isSubmitting={isCreateNotePending}
        />
      </div>

      <div className="flex-1 space-y-6 md:space-y-8 px-4 md:px-6 w-full max-w-4xl mx-auto min-h-screen overflow-x-hidden">
        {unassignedNotes.length > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <InboxIcon className="h-4 w-4" />
                Unassigned Notes
                <Badge variant="secondary" className="ml-2">
                  {unassignedNotes.length}
                </Badge>
              </h2>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              These notes aren't in any list yet. Add them to a list to organize
              them.
            </p>

            <SimpleNoteTable
              notes={unassignedNotes}
              renderActions={(note) => (
                <AddToListDropdown
                  lists={lists}
                  onSelect={(listId) => handleAddToList(note.id, listId)}
                  isLoading={addNoteToList.isPending}
                />
              )}
            />
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="space-y-3">
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
              <h3 className="text-xl font-display font-semibold">
                All notes organized!
              </h3>
              <p className="text-muted-foreground max-w-sm mx-auto">
                Every note is assigned to at least one list. Create a new note
                to get started.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

type AddToListDropdownProps = {
  lists: Array<{ id: string; name: string; icon?: string | null }>;
  onSelect: (listId: string) => void;
  isLoading?: boolean;
};

function AddToListDropdown({
  lists,
  onSelect,
  isLoading,
}: AddToListDropdownProps) {
  if (lists.length === 0) {
    return (
      <Button variant="ghost" size="sm" disabled className="h-7 px-2 text-xs">
        <FolderPlus className="h-3 w-3 mr-1" />
        No lists
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <FolderPlus className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Add to list</span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {lists.map((list) => (
          <DropdownMenuItem key={list.id} onClick={() => onSelect(list.id)}>
            {list.icon && <span className="mr-2">{list.icon}</span>}
            {list.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
